/**
 * BrainRunDrawer - Phase V3-1
 * 
 * Displays the output of a BrainRun - the unified intelligence engine.
 * Replaces workflow-specific views with a single authoritative presentation.
 * 
 * Sections:
 * 1. Quality + Confidence
 * 2. What We Know (metrics)
 * 3. What We Can Conclude (assertive)
 * 4. What To Do (recommendations + impact)
 * 5. If Ignored (downside scenario)
 * 6. Evidence Panel
 * 7. Create Actions
 * 8. Debug (collapsed)
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, CheckCircle, AlertTriangle, AlertCircle, Sparkles,
  BarChart3, Target, TrendingDown, Loader2, Brain,
  ChevronDown, ChevronUp, Save, ListTodo, FileDown,
  Maximize2, Minimize2,
} from 'lucide-react';

// Types from API
interface NormalizedMetric {
  metricKey: string;
  label: string;
  value: number | null;
  unit: string;
  dataStatus: 'known' | 'unavailable';
  displayValue: string;
}

interface DQERecommendation {
  type: string;
  title: string;
  ownerRole: string;
  timeHorizon: string;
  rationale: string;
  confidence: string;
}

interface ImpactSummary {
  upsideRange?: [number, number];
  downsideRange?: [number, number];
  confidence: number;
}

// V3-4B + V3-4C: Promo context from BrainRun (retailer + SKU level)
interface PromoContext {
  activePromoCount: number;
  promosByRetailer: Array<{
    retailerName: string;
    promos: string[];
    liftPct: number | null;
    pullForwardDetected: boolean;
  }>;
  anyPullForward: boolean;
  aggregateLiftPct: number | null;
  // V3-4C: SKU-level promo intel
  heroSkus?: Array<{
    skuId: string;
    skuName: string;
    promoName: string;
    liftPct: number;
    incrementalRevenue: number;
  }>;
  pullForwardSkus?: Array<{
    skuId: string;
    skuName: string;
    promoName: string;
    decayPct: number;
  }>;
  heroConcentrationPct?: number;
}

// V3-3C: Learning context
interface LearningContext {
  calibrationApplied: Record<string, number>;
  provenPlays: string[];
  outcomeCount: number;
}

// V3-5: Forecast context
interface ForecastAdjustmentDisplay {
  retailerName: string;
  skuName?: string;
  fiscalWeek: string;
  baseForecast: number;
  adjustedForecast: number;
  adjustmentPct: number;
  reason: string;
}

interface ForecastContext {
  totalAdjustments: number;
  totalForecastDelta: number;
  avgConfidence: number;
  upliftAdjustments: ForecastAdjustmentDisplay[];
  decayAdjustments: ForecastAdjustmentDisplay[];
}

interface BrainOutput {
  runId: string;
  companyId: string;
  createdAt: string;
  trigger: string;
  normalizedMetrics: NormalizedMetric[];
  conclusions: string[];
  recommendations: DQERecommendation[];
  impactSummary: ImpactSummary;
  evidenceBundleId: string | null;
  conflicts: string[];
  qualityScore: number;
  coveragePct: number;
  autoRepairActions: any[];
  retailersAnalyzed: string[];
  fiscalWeek: string;
  executionTimeMs: number;
  promoContext?: PromoContext; // V3-4B
  learningContext?: LearningContext; // V3-3C
  forecastContext?: ForecastContext; // V3-5
}

interface BrainRunError {
  errorCode: string;
  attemptId: string;
  stage: string;
  message: string;
  details?: string;
}

interface BrainRunDrawerProps {
  brainOutput: BrainOutput | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: BrainRunError | null;
}

export function BrainRunDrawer({ brainOutput, isOpen, onClose, isLoading, error }: BrainRunDrawerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [copiedDebug, setCopiedDebug] = useState(false);
  const [isCreatingActions, setIsCreatingActions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const queryClient = useQueryClient();
  
  if (!isOpen) return null;
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  const getQualityStatus = (score: number) => {
    if (score >= 80) return 'PASS';
    if (score >= 50) return 'DEGRADED';
    return 'FAIL';
  };
  
  const handleSave = async () => {
    if (!brainOutput) return;
    setIsSaving(true);
    setFlash(null);
    
    try {
      const resp = await fetch('/api/command-center/workflow-artifacts/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: brainOutput.runId,
          workflowId: 'brain_run',
          executiveSummary: brainOutput.conclusions.join(' • '),
          decisionPlan: { 
            topActions: brainOutput.recommendations,
            // Include full brain output for artifact viewer
            brainOutput: brainOutput,
          },
          quality: {
            status: getQualityStatus(brainOutput.qualityScore),
            score: brainOutput.qualityScore,
            coveragePctKnown: brainOutput.coveragePct,
            strictness: 'brain',
          },
          conflicts: brainOutput.conflicts,
          evidenceBundleId: brainOutput.evidenceBundleId,
          // Store normalized metrics and impact for artifact view
          normalizedMetrics: brainOutput.normalizedMetrics,
          impactSummary: brainOutput.impactSummary,
        }),
      });
      
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setFlash('Saved to Command Center');
      queryClient.invalidateQueries({ queryKey: ['workflow-artifacts'] });
    } catch (e) {
      console.error('[BrainRunDrawer] Save failed:', e);
      setFlash('Save failed');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCreateActions = async () => {
    if (!brainOutput) return;
    setIsCreatingActions(true);
    setFlash(null);
    
    try {
      const resp = await fetch('/api/command-center/actions/createFromDecisionPlan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: brainOutput.runId,
          workflowId: 'brain_run',
          brainRunId: brainOutput.runId,
          items: brainOutput.recommendations.map(r => ({
            title: r.title,
            rationale: r.rationale,
            recommendedOwnerRole: r.ownerRole,
            timeHorizon: r.timeHorizon,
          })),
        }),
      });
      
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setFlash(`Created ${data.createdCount ?? 0} actions`);
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
    } catch (e) {
      console.error('[BrainRunDrawer] Create actions failed:', e);
      setFlash('Create actions failed');
    } finally {
      setIsCreatingActions(false);
    }
  };
  
  const qualityStatus = brainOutput ? getQualityStatus(brainOutput.qualityScore) : 'DEGRADED';
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      
      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full bg-white shadow-xl z-50 overflow-y-auto transition-all duration-300 ${
        isExpanded ? 'w-full max-w-4xl' : 'w-full max-w-lg'
      }`}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Brain Intelligence</h2>
                <p className="text-sm text-gray-500">
                  {brainOutput ? `Week ${brainOutput.fiscalWeek} • ${brainOutput.retailersAnalyzed.join(', ')}` : 'Running...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
          
          {/* Action buttons */}
          {brainOutput && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 text-sm font-medium"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save to Command Center
              </button>
              
              <button
                onClick={handleCreateActions}
                disabled={isCreatingActions}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
              >
                {isCreatingActions ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListTodo className="w-4 h-4" />}
                Create Action List
              </button>
              
              {flash && (
                <span className={`text-sm font-medium ${flash.includes('failed') ? 'text-red-600' : 'text-emerald-600'}`}>
                  {flash}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error State */}
          {error ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-red-900">Brain Run Failed</h3>
                    <p className="text-sm text-red-800 mt-1">{error.message}</p>
                    
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-red-700">Stage:</span>
                        <span className="px-2 py-0.5 bg-red-100 rounded text-red-800 font-mono">{error.stage}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-red-700">Attempt ID:</span>
                        <span className="font-mono text-red-800">{error.attemptId}</span>
                      </div>
                      {error.details && (
                        <div className="mt-2">
                          <span className="font-medium text-red-700 text-xs">Details:</span>
                          <pre className="mt-1 p-2 bg-red-100 rounded text-xs text-red-900 font-mono overflow-x-auto whitespace-pre-wrap">
                            {error.details}
                          </pre>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => {
                        const debugInfo = JSON.stringify({
                          errorCode: error.errorCode,
                          attemptId: error.attemptId,
                          stage: error.stage,
                          message: error.message,
                          details: error.details,
                          timestamp: new Date().toISOString(),
                        }, null, 2);
                        navigator.clipboard.writeText(debugInfo);
                        setCopiedDebug(true);
                        setTimeout(() => setCopiedDebug(false), 2000);
                      }}
                      className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-100 text-red-800 hover:bg-red-200 text-sm font-medium"
                    >
                      {copiedDebug ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Copy Debug Info
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p>The Brain run encountered an error. You can:</p>
                <ul className="mt-2 space-y-1 list-disc list-inside text-gray-500">
                  <li>Click "Copy Debug Info" and share with support</li>
                  <li>Close this drawer and try again</li>
                  <li>Check the browser console for more details</li>
                </ul>
              </div>
            </div>
          ) : isLoading || !brainOutput ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
              <div className="text-gray-600">Running Brain intelligence...</div>
            </div>
          ) : (
            <>
              {/* 1. QUALITY BANNER */}
              <div className={`p-4 rounded-lg border ${
                qualityStatus === 'PASS' ? 'bg-green-50 border-green-200'
                  : qualityStatus === 'DEGRADED' ? 'bg-amber-50 border-amber-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {qualityStatus === 'PASS' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : qualityStatus === 'DEGRADED' ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <div className="font-semibold text-gray-900">
                        {qualityStatus === 'PASS' ? 'Verified Intelligence'
                          : qualityStatus === 'DEGRADED' ? 'Intelligence Complete — Some Data Missing'
                          : 'Intelligence Incomplete'}
                      </div>
                      <div className="text-xs text-gray-600">
                        Coverage: {Math.round(brainOutput.coveragePct * 100)}% • 
                        Score: {brainOutput.qualityScore}/100 • 
                        Confidence: {Math.round(brainOutput.impactSummary.confidence * 100)}%
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 text-right">
                    <div className="font-mono">{brainOutput.runId.slice(0, 16)}...</div>
                    <div className="text-gray-500">{brainOutput.executionTimeMs}ms</div>
                  </div>
                </div>
              </div>
              
              {/* 1b. LEARNING APPLIED BANNER (V3-3C) */}
              {brainOutput.learningContext && (
                brainOutput.learningContext.provenPlays.length > 0 || 
                Object.keys(brainOutput.learningContext.calibrationApplied).length > 0
              ) && (
                <div className="p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className="font-medium text-blue-900 text-sm">Learning Applied</span>
                  </div>
                  <div className="text-xs text-blue-700">
                    Confidence calibrated from {Object.keys(brainOutput.learningContext.calibrationApplied).length} action type{Object.keys(brainOutput.learningContext.calibrationApplied).length !== 1 ? 's' : ''} outcome history
                    {brainOutput.learningContext.provenPlays.length > 0 && (
                      <span className="ml-1">
                        • <span className="font-semibold">{brainOutput.learningContext.provenPlays.length}</span> proven play{brainOutput.learningContext.provenPlays.length !== 1 ? 's' : ''}: {brainOutput.learningContext.provenPlays.slice(0, 2).join(', ')}{brainOutput.learningContext.provenPlays.length > 2 ? '...' : ''}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* 2. WHAT WE KNOW (Metrics) */}
              {brainOutput.normalizedMetrics.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    What We Know
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {brainOutput.normalizedMetrics.slice(0, 8).map((metric, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">{metric.label}</div>
                        <div className="font-semibold text-gray-900">{metric.displayValue}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 2b. PROMO CONTEXT (V3-4B) */}
              {brainOutput.promoContext && brainOutput.promoContext.activePromoCount > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                    </svg>
                    Promo Context
                    {brainOutput.promoContext.anyPullForward && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        Pull-Forward Detected
                      </span>
                    )}
                  </h3>
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm text-gray-700">
                        <span className="font-semibold">{brainOutput.promoContext.activePromoCount}</span> active promotion{brainOutput.promoContext.activePromoCount !== 1 ? 's' : ''}
                      </div>
                      {brainOutput.promoContext.aggregateLiftPct !== null && (
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          brainOutput.promoContext.aggregateLiftPct >= 10 
                            ? 'bg-emerald-100 text-emerald-700'
                            : brainOutput.promoContext.aggregateLiftPct >= 0
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {brainOutput.promoContext.aggregateLiftPct >= 0 ? '+' : ''}{brainOutput.promoContext.aggregateLiftPct.toFixed(1)}% lift
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {brainOutput.promoContext.promosByRetailer.map((retailer, idx) => (
                        <div key={idx} className="bg-white/60 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-800">{retailer.retailerName}</div>
                            <div className="text-xs text-gray-500">{retailer.promos.slice(0, 2).join(', ')}{retailer.promos.length > 2 ? ` +${retailer.promos.length - 2} more` : ''}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {retailer.liftPct !== null && (
                              <span className={`text-sm font-medium ${retailer.liftPct >= 10 ? 'text-emerald-600' : retailer.liftPct >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {retailer.liftPct >= 0 ? '+' : ''}{retailer.liftPct.toFixed(1)}%
                              </span>
                            )}
                            {retailer.pullForwardDetected && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">⚠️ Pull-forward</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* V3-4C: Hero SKUs */}
                    {brainOutput.promoContext.heroSkus && brainOutput.promoContext.heroSkus.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                          🏆 Promo Hero SKUs
                          {brainOutput.promoContext.heroConcentrationPct && (
                            <span className="ml-2 font-normal lowercase">({brainOutput.promoContext.heroConcentrationPct.toFixed(0)}% of lift)</span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {brainOutput.promoContext.heroSkus.slice(0, 5).map((sku, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-emerald-50/50 rounded px-2 py-1">
                              <div className="truncate max-w-[60%]" title={sku.skuName}>
                                {sku.skuName}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-emerald-700 font-medium">+{sku.liftPct.toFixed(0)}%</span>
                                <span className="text-gray-500">${sku.incrementalRevenue.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* V3-4C: Pull-Forward SKUs */}
                    {brainOutput.promoContext.pullForwardSkus && brainOutput.promoContext.pullForwardSkus.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                          ⚠️ Pull-Forward Risk SKUs
                        </div>
                        <div className="space-y-1">
                          {brainOutput.promoContext.pullForwardSkus.slice(0, 3).map((sku, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-amber-50/50 rounded px-2 py-1">
                              <div className="truncate max-w-[70%]" title={sku.skuName}>
                                {sku.skuName}
                              </div>
                              <div className="text-amber-700 font-medium shrink-0">
                                -{sku.decayPct.toFixed(0)}% post-promo
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* 2b. FORECAST CONTEXT (V3-5) */}
              {brainOutput.forecastContext && brainOutput.forecastContext.totalAdjustments > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3v18h18" />
                      <path d="M18 17l-5-8-4 5-5-2" />
                    </svg>
                    Forecast Adjusted
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {brainOutput.forecastContext.totalAdjustments} adjustment{brainOutput.forecastContext.totalAdjustments !== 1 ? 's' : ''}
                    </span>
                    {brainOutput.forecastContext.totalForecastDelta !== 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        brainOutput.forecastContext.totalForecastDelta > 0 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {brainOutput.forecastContext.totalForecastDelta > 0 ? '+' : ''}
                        ${brainOutput.forecastContext.totalForecastDelta.toLocaleString()}
                      </span>
                    )}
                  </h3>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm text-blue-800 mb-3">
                      Promo intelligence applied to near-term forecasts. 
                      Confidence: {(brainOutput.forecastContext.avgConfidence * 100).toFixed(0)}%
                    </div>
                    
                    {/* Uplift adjustments */}
                    {brainOutput.forecastContext.upliftAdjustments.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                          📈 Forecast Uplifts (Promo Lift)
                        </div>
                        <div className="space-y-1">
                          {brainOutput.forecastContext.upliftAdjustments.slice(0, 3).map((adj, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-white/60 rounded px-2 py-1">
                              <div className="truncate max-w-[50%]" title={adj.skuName || adj.retailerName}>
                                {adj.skuName || adj.retailerName} ({adj.fiscalWeek})
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-gray-500">${adj.baseForecast.toLocaleString()}</span>
                                <span className="text-emerald-700">→</span>
                                <span className="text-emerald-700 font-medium">${adj.adjustedForecast.toLocaleString()}</span>
                                <span className="text-xs text-emerald-600">+{adj.adjustmentPct}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Decay adjustments */}
                    {brainOutput.forecastContext.decayAdjustments.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                          📉 Forecast Reductions (Pull-Forward)
                        </div>
                        <div className="space-y-1">
                          {brainOutput.forecastContext.decayAdjustments.slice(0, 2).map((adj, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-white/60 rounded px-2 py-1">
                              <div className="truncate max-w-[50%]" title={adj.skuName || adj.retailerName}>
                                {adj.skuName || adj.retailerName} ({adj.fiscalWeek})
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-gray-500">${adj.baseForecast.toLocaleString()}</span>
                                <span className="text-amber-700">→</span>
                                <span className="text-amber-700 font-medium">${adj.adjustedForecast.toLocaleString()}</span>
                                <span className="text-xs text-amber-600">{adj.adjustmentPct}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* 3. WHAT WE CAN CONCLUDE */}
              {brainOutput.conclusions.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    What We Can Conclude
                  </h3>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {brainOutput.conclusions.map((conclusion, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <span className="text-violet-500 mt-0.5">•</span>
                          <span>{conclusion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* 4. WHAT TO DO (Recommendations) */}
              {brainOutput.recommendations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-600" />
                    What To Do
                  </h3>
                  <div className="space-y-3">
                    {brainOutput.recommendations.map((rec, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{rec.title}</div>
                            <div className="text-sm text-gray-700 mt-1">{rec.rationale}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-700">
                              {rec.ownerRole}
                            </span>
                            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs">
                              {rec.timeHorizon.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              rec.confidence === 'high' ? 'bg-green-100 text-green-700'
                                : rec.confidence === 'medium' ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {rec.confidence} confidence
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 5. IF IGNORED (Downside Scenario) */}
              {brainOutput.impactSummary.downsideRange && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    If Ignored
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-red-900">
                      <span className="font-semibold">
                        {formatCurrency(brainOutput.impactSummary.downsideRange[0])} - {formatCurrency(brainOutput.impactSummary.downsideRange[1])}
                      </span>
                      {' '}at risk if recommendations are not followed.
                    </div>
                    {brainOutput.impactSummary.upsideRange && (
                      <div className="text-sm text-red-800 mt-2">
                        Potential upside opportunity: {formatCurrency(brainOutput.impactSummary.upsideRange[0])} - {formatCurrency(brainOutput.impactSummary.upsideRange[1])}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* 6. CONFLICTS (if any) */}
              {brainOutput.conflicts.length > 0 && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-900">Data Conflicts Detected</span>
                  </div>
                  <div className="text-sm text-amber-800">
                    {brainOutput.conflicts.length} metric(s) had conflicting values. Using preferred source.
                  </div>
                </div>
              )}
              
              {/* 7. DEBUG PANEL */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowDebug(!showDebug)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showDebug ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Debug Details
                </button>
                
                {showDebug && (
                  <div className="mt-3 bg-gray-50 rounded-lg p-4 text-xs font-mono overflow-x-auto">
                    <pre>{JSON.stringify({
                      runId: brainOutput.runId,
                      trigger: brainOutput.trigger,
                      fiscalWeek: brainOutput.fiscalWeek,
                      retailersAnalyzed: brainOutput.retailersAnalyzed,
                      metricsCount: brainOutput.normalizedMetrics.length,
                      recommendationsCount: brainOutput.recommendations.length,
                      conclusionsCount: brainOutput.conclusions.length,
                      conflictsCount: brainOutput.conflicts.length,
                      autoRepairCount: brainOutput.autoRepairActions.length,
                      evidenceBundleId: brainOutput.evidenceBundleId,
                      executionTimeMs: brainOutput.executionTimeMs,
                    }, null, 2)}</pre>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default BrainRunDrawer;

